import { Redirect } from '_app/components/Redirect'
import { IconAws } from '_app/icons/IconAws'
import { IconExplorer } from '_app/icons/IconExplorer'
import { IconMetrics } from '_app/icons/IconMetrics'
import { ErrorBoundary } from 'common/components/ErrorBoundary'
import {
  ArrowRightLeft,
  Bell,
  Calculator,
  Gauge,
  ClipboardList,
  FunctionSquare,
  Key,
  KeyIcon,
  KeyRound,
  Layers,
  LayoutGrid,
  Rocket,
  Settings,
  User,
  Users,
} from 'lucide-react'
import { lazy } from 'react'
import { createBrowserRouter, useParams } from 'react-router-dom'
import { RequireOrgAdmin } from 'settings/RequireOrgAdmin'

/**
 * This handles all routing for the app.
 * We utilize the 'handle' property to enrich routes with breadcrumbs and enhance each route with additional information and data.
 * We load the routes lazily to improve the initial load time of the app.
 */

/**
 * Redirect from apps/* to services/* while preserving the wildcard path
 */
const AppsToServicesRedirect = () => {
  const params = useParams()
  const wildcardPath = params['*'] || ''
  return <Redirect to={`../services/${wildcardPath}`} replace />
}

/**
 * Catch-all for unknown /:orgName/settings/* paths. The redirect must be
 * absolute: a relative `to` inside a splat route resolves against the full
 * matched path in react-router v7, so it appends a segment on every render
 * and redirects forever (/settings/x -> /settings/x/general -> ...)
 */
const SettingsCatchAllRedirect = () => {
  const { orgName } = useParams()
  return <Redirect to={`/${orgName}/settings/general`} replace />
}

/**
 * The 'Root' wraps the entire app with all required providers and private route
 */
const Root = lazy(() => import('./_app/components/Root'))
const SettingsHeader = lazy(() => import('./settings/SettingsHeader'))
const InstanceHeader = lazy(() => import('./instances/components/instance/InstanceHeader'))
const InstancePage = lazy(() => import('./instances/InstancePage'))
const NotFoundPage = lazy(() => import('./_app/components/NotFoundPage'))
const Layout = lazy(() => import('./_app/components/Layout'))
/**
 * Root level pages
 */
const OptOutPage = lazy(() => import('./_app/components/OptOutPage'))
const GithubCallbackPage = lazy(() => import('./providers/components/GithubCallbackPage'))
const JoinOrganization = lazy(() => import('./settings/team/components/JoinOrganization'))
const SsoLogin = lazy(() => import('./auth/components/SsoLogin'))
const Components = lazy(() => import('./Components'))

/**
 * Organization level pages
 */
const AccountSettings = lazy(() => import('./settings/general/components/AccountSettings'))
const DeploymentsPage = lazy(() => import('./deployments/DeploymentsPage'))

/**
 * Settings level pages
 */
const GeneralSettingsPage = lazy(() => import('./settings/general/GeneralSettingsPage'))
const TeamMembersPage = lazy(() => import('./settings/team/TeamPage'))
const ProvidersListPage = lazy(() => import('./providers/ProvidersListPage'))
const AccessKeysPage = lazy(() => import('./settings/accessKeys/AccessKeysPage'))
const LicenseKeysPage = lazy(() => import('./settings/licenseKeys/LicenseKeysPage'))
const BillingPage = lazy(() => import('./settings/billing/BillingPage'))
const UsagePage = lazy(() => import('./settings/usage/UsagePage'))
const Alerts = lazy(() => import('./settings/alerts/AlertsPage'))
const Integrations = lazy(() => import('./settings/integrations/Integrations'))

const ExplorerPage = lazy(() => import('./explorer/ExplorerPage'))
const MetricsPage = lazy(() => import('./metrics/components/Metrics'))

/**
 * Apps/Service level pages
 */
const InstanceDeployments = lazy(() =>
  import('./instances/components/instance/InstanceDeployments')
)
const ParametersListPage = lazy(() => import('./parameters/ParametersListPage'))
const ServicesListPage = lazy(() => import('./services/ServicesListPage'))
const ServiceSettingsHeader = lazy(() => import('./services/ServiceSettingsPage'))
const CiCdSettings = lazy(() => import('./deployments/components/CiCdSettings'))

/**
 * Instance level pages (also used by apps route for backwards compatibility)
 */
const InstanceMetrics = lazy(() => import('./instances/components/instance/InstanceMetrics'))
const InstanceOutputs = lazy(() => import('./instances/components/instance/InstanceOutputs'))

export const settingsSubPages = [
  {
    path: 'general',
    element: <GeneralSettingsPage />,
    handle: {
      path: 'general',
      label: 'General',
      icon: <Settings size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'team',
    element: <TeamMembersPage />,
    handle: {
      path: 'team',
      label: 'Team',
      icon: <Users size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'providers',
    element: <ProvidersListPage />,
    handle: {
      path: 'providers',
      label: 'Providers',
      icon: <IconAws size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'alerts',
    element: <Alerts />,
    handle: {
      path: 'alerts',
      label: 'Alerts',
      icon: <Bell size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'integrations',
    element: <Integrations />,
    handle: {
      path: 'integrations',
      label: 'Integrations',
      icon: <Layers size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'accessKeys',
    element: <AccessKeysPage />,
    handle: {
      path: 'accessKeys',
      label: 'Access Keys',
      icon: <Key size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'licenseKeys',
    element: (
      <RequireOrgAdmin feature="License Keys">
        <LicenseKeysPage />
      </RequireOrgAdmin>
    ),
    handle: {
      path: 'licenseKeys',
      label: 'License Keys',
      icon: <KeyRound size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'usage',
    element: <UsagePage />,
    handle: {
      path: 'usage',
      label: 'Usage',
      icon: <Gauge size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'billing',
    element: (
      <RequireOrgAdmin feature="Billing">
        <BillingPage />
      </RequireOrgAdmin>
    ),
    handle: {
      path: 'billing',
      label: 'Billing',
      icon: <Calculator size={14} />,
      showBreadCrumb: true,
    },
  },
]
export const serviceSettingsSubPages = [
  {
    path: 'cicd',
    element: <CiCdSettings />,
    handle: {
      path: 'cicd',
      label: 'CI/CD',
      icon: <Rocket size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'providers',
    element: <ProvidersListPage linkType="service" />,
    handle: {
      path: 'providers',
      label: 'Providers',
      icon: <IconAws size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'parameters',
    element: <ParametersListPage linkType="service" />,
    handle: {
      path: 'parameters',
      label: 'Parameters',
      icon: <KeyIcon size={18} />,
      showBreadCrumb: true,
    },
  },
]

export const instanceSubPages = [
  {
    path: 'overview',
    element: <MetricsPage />,
    handle: {
      isMonitoringView: true,
      path: 'overview',
      label: 'Overview',
      icon: <IconMetrics size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'explorer',
    element: <ExplorerPage />,
    handle: {
      isMonitoringView: true,
      path: 'explorer',
      label: 'Explorer',
      icon: <IconExplorer size={14} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'functions',
    element: <InstanceMetrics />,
    handle: {
      isMonitoringView: true,
      path: 'functions',
      label: 'Functions',
      icon: <FunctionSquare size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'endpoints',
    element: <InstanceMetrics />,
    handle: {
      isMonitoringView: true,
      path: 'endpoints',
      label: 'API',
      icon: <ArrowRightLeft size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'deploys',
    element: <InstanceDeployments />,
    handle: {
      path: 'deploys',
      label: 'Deploys',
      icon: <Rocket size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'deploys/:deploymentId',
    element: <InstanceDeployments />,
    handle: {
      hideTabBar: true,
      path: 'deploys',
      label: 'Deploys',
      icon: <Rocket size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'outputs',
    element: <InstanceOutputs />,
    handle: {
      path: 'outputs',
      label: 'Outputs',
      icon: <ClipboardList size={15} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'parameters',
    element: <ParametersListPage linkType="instance" parentLinkType="service" />,
    handle: {
      path: 'parameters',
      label: 'Parameters',
      icon: <KeyIcon size={18} />,
      showBreadCrumb: true,
    },
  },
  {
    path: 'providers',
    element: <ProvidersListPage linkType="instance" parentLinkType="service" />,
    handle: {
      path: 'providers',
      label: 'Providers',
      icon: <IconAws size={15} />,
      showBreadCrumb: true,
    },
  },
]

export const routes = [
  {
    path: '/',
    element: <Root />, // This is the root component for the app. It wraps the entire app with all required providers
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: 'opt-out',
        element: <OptOutPage />,
        handle: {
          public: true, // Page can be show if you're unauthenticated
        },
      },
      {
        path: 'git/callback',
        element: <GithubCallbackPage />,
      },
      {
        path: 'join',
        element: <JoinOrganization />,
      },
      {
        // Dedicated SSO entry point: /sso/<connectionName>. Redirects straight
        // to the customer's Auth0 enterprise connection (their IdP). Public so
        // it renders for unauthenticated users.
        path: 'sso/:connection',
        element: <SsoLogin />,
        handle: {
          public: true,
        },
      },
      {
        // The account page is user-scoped, not org-scoped, so it lives at
        // /me. Static segments outrank the :orgName param, and 'me' is
        // already a reserved word for org names (useOrgNameInUrl derives
        // reserved words from the org child routes). The Layout sidebar
        // falls back to the user's preferred org.
        path: 'me',
        element: <Layout />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            element: <AccountSettings />,
            handle: {
              path: 'me',
              label: 'Account',
              icon: <User size={14} />,
              showBreadCrumb: true,
            },
          },
        ],
      },
      {
        path: ':orgName',
        element: <Layout />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: 'components',
            element: <Components />,
          },
          {
            index: true, // Redirect to :orgName/services
            element: <Redirect to="services" replace />,
          },
          {
            path: 'apps', // Legacy redirect from /apps to /services
            element: <Redirect to="../services" replace />,
          },
          {
            path: 'apps/*', // Legacy redirect from /apps/* to /services with path preservation
            element: <AppsToServicesRedirect />,
          },

          {
            path: 'me', // Legacy redirect: the account page moved to /me
            element: <Redirect to="/me" replace />,
          },
          {
            path: 'services',
            errorElement: <ErrorBoundary />,
            handle: {
              showInSidebar: true,
              path: 'services',
              label: 'Services',
              icon: <LayoutGrid size={14} />,
              showBreadCrumb: true,
            },
            children: [
              {
                index: true, // Show services page on /:orgName/services
                element: <ServicesListPage />,
                handle: {
                  path: 'services',
                  label: 'Services',
                  icon: <LayoutGrid size={14} />,
                },
              },
              {
                path: ':appName/:serviceName/settings',
                errorElement: <ErrorBoundary />,
                handle: {
                  showBreadCrumb: true,
                  showServiceSettingsBreadCrumbs: true,
                  header: <ServiceSettingsHeader />,
                  path: 'settings',
                  label: 'Settings',
                  icon: <Settings size={14} />,
                },
                children: [
                  {
                    index: true, // Redirect to /services/:appName/:serviceName/settings/cicd
                    element: <Redirect to="cicd" replace />,
                  },
                  ...serviceSettingsSubPages,
                ],
              },
              {
                path: ':appName/:serviceName/:stage/:region',
                element: <InstancePage />,
                errorElement: <ErrorBoundary />,
                handle: {
                  showBreadCrumb: true,
                  showServiceBreadCrumbs: true,
                  header: <InstanceHeader />,
                },
                children: [
                  {
                    index: true, // Redirect to overview
                    element: <Redirect to="overview" replace />,
                  },
                  ...instanceSubPages,
                ],
              },
            ],
          },
          {
            path: 'metrics',
            label: 'Metrics',
            element: <MetricsPage />,
            handle: {
              isMonitoringView: true,
              showInSidebar: true,
              path: 'metrics',
              label: 'Metrics',
              icon: <IconMetrics size={15} />,
              showBreadCrumb: true,
            },
          },
          {
            path: 'explorer',
            label: 'Explorer',
            element: <ExplorerPage />,
            handle: {
              isMonitoringView: true,
              showInSidebar: true,
              path: 'explorer',
              label: 'Explorer',
              icon: <IconExplorer size={15} />,
              showBreadCrumb: true,
            },
          },
          {
            path: 'deploys',
            label: 'Deploys',
            element: <DeploymentsPage />,
            handle: {
              showInSidebar: true,
              path: 'deploys',
              label: 'CI/CD Deploys',
              icon: <Rocket size={15} />,
              showBreadCrumb: true,
            },
          },

          {
            path: 'settings',
            label: 'Settings',
            errorElement: <ErrorBoundary />,
            handle: {
              showInSidebar: true,
              header: <SettingsHeader />,
              path: 'settings',
              label: 'Settings',
              icon: <Settings size={14} />,
              showBreadCrumb: true,
            },
            children: [
              {
                index: true, // Redirect to /settings/general
                element: <Redirect to="general" replace />,
              },
              {
                path: '*', // Redirect to /settings/general
                element: <SettingsCatchAllRedirect />,
              },
              ...settingsSubPages,
            ],
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
      {
        path: '/*',
        element: <NotFoundPage />,
      },
    ],
  },
]

export const router = createBrowserRouter(routes)
